import { useNavigation } from "@react-navigation/native";
import { FC, useCallback, useMemo, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import {
  tApplicationContactPath,
  tApplicationContactPhonePath,
  tApplicationState,
  tApplicationTypePath,
} from "src/redux/Application/types";
import { tReduxState } from "src/redux/types";
import { kPhonePattern } from "src/util/constants";
import { formatPhoneNumber } from "src/util/gFormatting";
import { tCompanyType } from "tmt_api/src/types/types";
import { kRoute } from "../kRoute";
import { tOption } from "./ComboBox";
import { tValidType, ValidatedComboBoxInput, ValidatedTextFormInput } from "./FormInputs";

type tCompanyElement = tOption<tCompanyType>;
export type tpApplication = {
  loggedIn: boolean;
  applicationType?: tApplicationState["type"] | "admin";
};
const rTypeSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.type;
const rEmailSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.contact.email;
const rFirstNameSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.contact.firstName;
const rLastNameSelector = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.contact.lastName;
const rPhoneSelector = ({ ApplicationReducer }: tReduxState) => formatPhoneNumber(ApplicationReducer.contact.phone);
const _isShown = ({ ApplicationReducer }: tReduxState) => ApplicationReducer.isShown;
export const kEmailPattern =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gim;

const UserApplicationInfo: FC<tpApplication> = ({ loggedIn }) => {
  const kNav = useNavigation();
  const [sIsValid, setIsValid] = useState<tValidType>({});
  const rEmailDisabled = useSelector(({ ApplicationReducer }) => ApplicationReducer.emailDisabled);
  const rType = useSelector(({ ApplicationReducer }) => ApplicationReducer.type);
  const aCompanyType: Array<tCompanyElement> = useMemo(
    () => [
      { myLabel: "A Brand", myValue: "brand", isDefault: rType === "brand" },
      { myLabel: "A Retailer", myValue: "retailer", isDefault: rType === "retailer" },
      { myLabel: "A Rep", myValue: "rep", isDefault: rType === "rep" },
    ],
    [rType],
  );

  const _normalizeCompany = useCallback(
    (myValue: string) => {
      const _normalizeCompanyMatch = (myIndex: tCompanyElement) => myIndex.myValue === myValue;
      const anIndex = aCompanyType.findIndex(_normalizeCompanyMatch);
      return anIndex >= 0 ? aCompanyType[anIndex].myValue : aCompanyType[0].myValue;
    },
    [aCompanyType],
  );

  const _navigateLogIn = useCallback(() => kNav.navigate(kRoute.P01), [kNav]);

  return (
    <View style={styles.gApplyTablePageContainer}>
      <View style={loggedIn ? styles.gFormContainer : styles.gFormContainerNoNav}>
        {!loggedIn && (
          <View style={styles.applyLogInText}>
            <Text style={styles.body}>If you have an account, </Text>
            <TouchableOpacity onPress={_navigateLogIn}>
              <Text style={[styles.body, styles.bodyLink]}>Login</Text>
            </TouchableOpacity>
          </View>
        )}
        <ValidatedComboBoxInput<tApplicationTypePath>
          wrapperStyle={styles.applyToJoinView}
          controller={sIsValid}
          setController={setIsValid}
          rules={{ required: { message: "Required" } }}
          isShown={_isShown}
          uniqueName="userType"
          myTitle="Apply to join as *"
          ComboOptions={aCompanyType}
          dispatchAction={{ type: "UPDATE_TYPE", newType: "brand" }}
          dispatchPath="newType"
          selectorFunction={rTypeSelector}
          titleProps={{ style: styles.sectionTitle }}
          normalizerFunction={_normalizeCompany}
          searchable={rEmailDisabled}
          placeholder="Select Your Company Type"
          autoComplete="off"
        />
        <View style={styles.applyUserFormContainer}>
          <Text style={styles.sectionTitle}>Member Information</Text>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tApplicationContactPath>
              wrapperStyle={styles.gLinkBankFormItem}
              titleViewStyle={styles.rowNoMargin}
              controller={sIsValid}
              setController={setIsValid}
              rules={{ required: { message: "Required" } }}
              isShown={_isShown}
              uniqueName="UserFirstName"
              myTitle="First Name *"
              selectorFunction={rFirstNameSelector}
              textInputProps={{
                style: styles.gLinkBankInput,
                autoCompleteType: "given-name",
                placeholder: "First Name",
              }}
              dispatchAction={{
                type: "UPDATE_CONTACT_APPLICATION",
                applicationPart: { contact: { firstName: "" } },
              }}
              dispatchPath="applicationPart.contact.firstName"
              titleProps={{ style: styles.P08_01_InputHeader }}
            />
            <ValidatedTextFormInput<tApplicationContactPath>
              wrapperStyle={styles.gLinkBankFormItemNoMargin}
              titleViewStyle={styles.rowNoMargin}
              controller={sIsValid}
              setController={setIsValid}
              rules={{ required: { message: "Required" } }}
              isShown={_isShown}
              uniqueName="UserLastName"
              myTitle="Last Name *"
              selectorFunction={rLastNameSelector}
              textInputProps={{
                style: styles.gLinkBankInput,
                placeholder: "Last Name",
                autoCompleteType: "family-name",
              }}
              dispatchAction={{
                type: "UPDATE_CONTACT_APPLICATION",
                applicationPart: { contact: { lastName: "" } },
              }}
              dispatchPath="applicationPart.contact.lastName"
              titleProps={{ style: styles.P08_01_InputHeader }}
            />
          </View>
          <View style={styles.gFormRowNoMargin}>
            <ValidatedTextFormInput<tApplicationContactPath>
              wrapperStyle={styles.gLinkBankFormItemNoBottomMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={sIsValid}
              setController={setIsValid}
              rules={{
                required: { message: "Required" },
                pattern: {
                  pattern: kEmailPattern,
                  message: "Please enter valid email",
                },
              }}
              isShown={_isShown}
              uniqueName="Email"
              myTitle="Email *"
              selectorFunction={rEmailSelector}
              dispatchAction={{
                type: "UPDATE_CONTACT_APPLICATION",
                applicationPart: { contact: { email: "" } },
              }}
              dispatchPath="applicationPart.contact.email"
              textInputProps={{
                editable: !rEmailDisabled,
                placeholder: "Your Company Email",
                autoCompleteType: "email",
                style: styles.gLinkBankInput,
              }}
            />
            <ValidatedTextFormInput<tApplicationContactPhonePath>
              wrapperStyle={styles.gLinkBankFormNoBottomMargin}
              titleViewStyle={styles.rowNoMargin}
              titleProps={{ style: styles.P08_01_InputHeader }}
              controller={sIsValid}
              setController={setIsValid}
              rules={{
                required: { message: "Required" },
                pattern: {
                  pattern: kPhonePattern,
                  message: "Please enter valid Phone Number",
                },
              }}
              dispatchAction={{
                type: "UPDATE_PHONE_APPLICATION",
                phoneNumber: "",
              }}
              dispatchPath="phoneNumber"
              isShown={_isShown}
              uniqueName="PhoneNumber"
              myTitle="Phone Number *"
              selectorFunction={rPhoneSelector}
              textInputProps={{
                maxLength: 14,
                autoCompleteType: "tel",
                placeholder: "(XXX) XXX-XXXX",
                style: styles.gLinkBankInput,
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

export default UserApplicationInfo;

const styles = StyleSheet.create({
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  applyLogInText: {
    alignSelf: "center",
    flexDirection: "row",
    marginBottom: 5 * kStyleConst.kSpacing,
    marginTop: 3 * kStyleConst.kSpacing,
  },
  applyToJoinView: {
    flexGrow: 1,
    justifyContent: "space-between",
    marginBottom: 2 * kStyleConst.kSpacing,
    zIndex: 12,
  },
  applyUserFormContainer: {
    marginTop: 5 * kStyleConst.kSpacing,
  },
  body: {
    ...kFonts.poppinsRegularMedium,
    color: kColors.brand500,
    letterSpacing: 1,
    lineHeight: 25,
  },
  bodyLink: {
    fontWeight: "700",
    textDecorationLine: "underline",
  },
  gApplyTablePageContainer: {
    backgroundColor: kColors.white,
    borderRadius: 8,
    flexGrow: 1,
    marginTop: 7 * kStyleConst.kSpacing,
    zIndex: 2,
  },
  gFormContainer: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    marginTop: 12 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormContainerNoNav: {
    marginBottom: 12 * kStyleConst.kSpacing,
    marginHorizontal: 13 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoBottomMargin: {
    flexGrow: 1,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormNoBottomMargin: {
    flexGrow: 1,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  rowNoMargin: {
    flexDirection: "row",
    marginTop: 0,
  },
  sectionTitle: {
    ...kFonts.ralewayLargeLight,
    alignSelf: "flex-start",
    marginBottom: 2 * kStyleConst.kSpacing,
  },
});
